import { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "../lib/api";
import {
  ArrowLeft, Plus, Trash, Star, Eye, EyeSlash, VideoCamera,
  Check, X as XIcon, ArrowSquareOut,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const CATEGORIES = [
  { id: "scam-alert", label: "Scam alert" },
  { id: "how-to", label: "How-to" },
  { id: "walkthrough", label: "Walkthrough" },
  { id: "carrier-story", label: "Carrier story" },
  { id: "other", label: "Other" },
];

export default function AdminTikTokPostsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addUrl, setAddUrl] = useState("");
  const [addCaption, setAddCaption] = useState("");
  const [addCategory, setAddCategory] = useState("scam-alert");
  const [addFeatured, setAddFeatured] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/tiktoks");
      setPosts(r.data?.posts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addUrl.trim()) return;
    setAdding(true);
    try {
      const r = await client.post("/admin/tiktoks", {
        url: addUrl.trim(),
        caption: addCaption.trim() || null,
        category: addCategory,
        featured: addFeatured,
      });
      if (r.data?.created === false) {
        toast.info("Already saved — loaded existing post");
      } else {
        toast.success("TikTok saved");
      }
      setAddUrl("");
      setAddCaption("");
      setAddFeatured(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed — check the URL");
    } finally {
      setAdding(false);
    }
  };

  const toggleFeatured = async (post) => {
    try {
      await client.patch(`/admin/tiktoks/${post.id}`, { featured: !post.featured });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title.slice(0, 60) || post.url}"?`)) return;
    try {
      await client.delete(`/admin/tiktoks/${post.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-tiktok-posts-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        <Link
          to="/app/admin"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-4"
          data-testid="admin-tiktok-back"
        >
          <ArrowLeft size={14} /> Back to admin
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
              Admin · Social proof
            </div>
            <h1 className="text-3xl font-black tracking-tight">
              Manage TikTok posts
            </h1>
            <p className="text-white/60 text-sm mt-2 max-w-2xl">
              Every TikTok you post to your @freightpilotai account should live here — it becomes permanent social proof on <Link to="/tiktoks" className="text-[#FF5A1F] hover:underline">/tiktoks</Link> plus a 3-video widget on the homepage.
            </p>
          </div>
          <Link
            to="/tiktoks"
            target="_blank"
            data-testid="admin-tiktok-view-public"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
          >
            <ArrowSquareOut size={12} weight="bold" /> View public /tiktoks
          </Link>
        </div>

        {/* Add form */}
        <form
          onSubmit={submitAdd}
          data-testid="admin-tiktok-add-form"
          className="rounded-2xl border-2 border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.05] p-5 sm:p-6 mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center">
              <Plus size={16} weight="bold" />
            </div>
            <div className="text-sm font-bold">Add a TikTok post</div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label htmlFor="add-url" className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1 block">TikTok URL</label>
              <input
                id="add-url"
                type="url"
                required
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                placeholder="https://www.tiktok.com/@freightpilotai/video/..."
                data-testid="admin-tiktok-add-url"
                className="w-full rounded-md bg-black/40 border border-white/10 text-white px-3 py-2.5 text-sm placeholder-white/30 focus:border-[#FF5A1F] focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="add-cat" className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1 block">Category</label>
              <select
                id="add-cat"
                value={addCategory}
                onChange={(e) => setAddCategory(e.target.value)}
                data-testid="admin-tiktok-add-category"
                className="w-full rounded-md bg-black/40 border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A1F] focus:outline-none"
              >
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="add-caption" className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1 block">Caption (optional)</label>
              <input
                id="add-caption"
                type="text"
                value={addCaption}
                onChange={(e) => setAddCaption(e.target.value)}
                placeholder="Ghost Logistics — MC-999888 · $4,200 lost"
                data-testid="admin-tiktok-add-caption"
                className="w-full rounded-md bg-black/40 border border-white/10 text-white px-3 py-2.5 text-sm placeholder-white/30 focus:border-[#FF5A1F] focus:outline-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-[13px] text-white/70 cursor-pointer">
              <input
                type="checkbox"
                checked={addFeatured}
                onChange={(e) => setAddFeatured(e.target.checked)}
                data-testid="admin-tiktok-add-featured"
                className="w-4 h-4 accent-[#FF5A1F]"
              />
              Feature on homepage widget
            </label>
            <button
              type="submit"
              disabled={adding || !addUrl.trim()}
              data-testid="admin-tiktok-add-submit"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold bg-[#FF5A1F] hover:bg-[#FF7038] text-black transition-colors disabled:opacity-50"
            >
              <Plus size={14} weight="bold" /> {adding ? "Fetching from TikTok…" : "Add post"}
            </button>
          </div>
        </form>

        {/* List */}
        {loading ? (
          <div className="text-white/50 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 p-10 text-center">
            <VideoCamera size={40} className="mx-auto text-white/25 mb-3" />
            <div className="text-white/70 font-bold mb-1">No TikToks saved yet</div>
            <div className="text-white/50 text-sm max-w-md mx-auto">
              Post your first DNP video to TikTok, paste the URL above, and it'll show on <code className="text-[#FF5A1F]">/tiktoks</code>.
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-tiktok-list">
            {posts.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-tiktok-row-${p.id}`}
                className={`rounded-xl border p-4 flex gap-4 transition-colors ${p.featured ? "border-emerald-500/40 bg-emerald-500/[0.03]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
              >
                {/* Thumbnail */}
                {p.thumbnail_url ? (
                  <img
                    src={p.thumbnail_url}
                    alt=""
                    className="w-20 h-28 sm:w-24 sm:h-32 rounded-md object-cover bg-black/40 border border-white/10 shrink-0"
                  />
                ) : (
                  <div className="w-20 h-28 sm:w-24 sm:h-32 rounded-md bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
                    <VideoCamera size={26} className="text-white/25" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest bg-[#FF5A1F]/20 border border-[#FF5A1F]/40 text-[#FF9764] font-bold px-2 py-0.5 rounded">
                      {CATEGORIES.find((c) => c.id === p.category)?.label || p.category}
                    </span>
                    {p.featured && (
                      <span className="text-[9px] uppercase tracking-widest bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold px-2 py-0.5 rounded inline-flex items-center gap-1">
                        <Star size={10} weight="fill" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-bold text-white leading-snug line-clamp-2">
                    {p.title || "(no title)"}
                  </div>
                  {p.caption && <div className="text-[12px] text-white/60 mt-1 line-clamp-2">{p.caption}</div>}
                  <div className="text-[11px] text-white/40 mt-1">
                    {p.author_name} · added {p.added_at?.slice(0, 10) || ""}
                  </div>

                  <div className="flex gap-2 mt-2">
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-tiktok-open-${p.id}`}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                    >
                      <ArrowSquareOut size={10} weight="bold" /> Open
                    </a>
                    <button
                      type="button"
                      onClick={() => toggleFeatured(p)}
                      data-testid={`admin-tiktok-feature-${p.id}`}
                      className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-bold transition-colors ${p.featured ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30" : "bg-white/10 hover:bg-emerald-500/20 hover:text-emerald-300 text-white border border-white/10"}`}
                    >
                      <Star size={10} weight={p.featured ? "fill" : "regular"} /> {p.featured ? "Unfeature" : "Feature"}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(p)}
                      data-testid={`admin-tiktok-delete-${p.id}`}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-bold bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/40 transition-colors ml-auto"
                    >
                      <Trash size={10} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
